import React, { useCallback, useRef, useState } from 'react';
import { Button, Col, Input, InputNumber, Row, Slider, Space, Typography } from 'antd';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';

const { Text } = Typography;

const VW = 1000;
const VH = 1000;

export interface GridData {
  angle: number;
  v_lines: number[];
  h_lines: number[];
  labels: Record<string, string>;
}

export const emptyGrid = (): GridData => ({ angle: 0, v_lines: [], h_lines: [], labels: {} });

export const parseGridData = (raw: any): GridData => {
  if (!raw || Array.isArray(raw)) return emptyGrid();
  return {
    angle: typeof raw.angle === 'number' ? raw.angle : 0,
    v_lines: Array.isArray(raw.v_lines) ? raw.v_lines : [],
    h_lines: Array.isArray(raw.h_lines) ? raw.h_lines : [],
    labels: (raw.labels && typeof raw.labels === 'object' && !Array.isArray(raw.labels))
      ? raw.labels : {},
  };
};

// ── Math ──────────────────────────────────────────────────────────────────────

const toRad = (deg: number) => (deg * Math.PI) / 180;

function clipLine(
  px: number, py: number, dx: number, dy: number
): [[number, number], [number, number]] | null {
  const eps = 1e-9;
  let sMin = -Infinity, sMax = Infinity;
  if (Math.abs(dx) > eps) {
    const s1 = -px / dx, s2 = (1 - px) / dx;
    sMin = Math.max(sMin, Math.min(s1, s2));
    sMax = Math.min(sMax, Math.max(s1, s2));
  } else if (px < -eps || px > 1 + eps) return null;
  if (Math.abs(dy) > eps) {
    const s1 = -py / dy, s2 = (1 - py) / dy;
    sMin = Math.max(sMin, Math.min(s1, s2));
    sMax = Math.min(sMax, Math.max(s1, s2));
  } else if (py < -eps || py > 1 + eps) return null;
  if (sMin > sMax + eps) return null;
  return [
    [px + sMin * dx, py + sMin * dy],
    [px + sMax * dx, py + sMax * dy],
  ];
}

function getLineEndpoints(
  t: number, type: 'col' | 'row', angleDeg: number
): [[number, number], [number, number]] | null {
  const θ = toRad(angleDeg);
  const sin = Math.sin(θ), cos = Math.cos(θ);
  const [dx, dy, nx, ny] = type === 'col'
    ? [sin, cos, cos, -sin]
    : [cos, -sin, sin, cos];
  return clipLine(t * nx, t * ny, dx, dy);
}

function projectToAxis(x: number, y: number, type: 'col' | 'row', angleDeg: number): number {
  const θ = toRad(angleDeg);
  const sin = Math.sin(θ), cos = Math.cos(θ);
  return type === 'col' ? x * cos - y * sin : x * sin + y * cos;
}

function getImageBounds(angleDeg: number) {
  const θ = toRad(angleDeg);
  const sin = Math.sin(θ), cos = Math.cos(θ);
  const corners: [number, number][] = [[0, 0], [1, 0], [0, 1], [1, 1]];
  const cp = corners.map(([x, y]) => x * cos - y * sin);
  const rp = corners.map(([x, y]) => x * sin + y * cos);
  return { colMin: Math.min(...cp), colMax: Math.max(...cp), rowMin: Math.min(...rp), rowMax: Math.max(...rp) };
}

function cellCenterInImage(
  c: number, r: number, angleDeg: number, colBounds: number[], rowBounds: number[]
): [number, number] {
  const u = (colBounds[c] + colBounds[c + 1]) / 2;
  const v = (rowBounds[r] + rowBounds[r + 1]) / 2;
  const θ = toRad(angleDeg);
  const sin = Math.sin(θ), cos = Math.cos(θ);
  return [u * cos + v * sin, -u * sin + v * cos];
}

function getCellCorners(
  c: number, r: number, angleDeg: number, colBounds: number[], rowBounds: number[]
): [number, number][] {
  const u1 = colBounds[c], u2 = colBounds[c + 1];
  const v1 = rowBounds[r], v2 = rowBounds[r + 1];
  const θ = toRad(angleDeg);
  const sin = Math.sin(θ), cos = Math.cos(θ);
  return [
    [u1 * cos + v1 * sin, -u1 * sin + v1 * cos],
    [u2 * cos + v1 * sin, -u2 * sin + v1 * cos],
    [u2 * cos + v2 * sin, -u2 * sin + v2 * cos],
    [u1 * cos + v2 * sin, -u1 * sin + v2 * cos],
  ];
}

function getCellAt(
  x: number, y: number, angleDeg: number,
  v_lines: number[], h_lines: number[],
  colCount: number, rowCount: number
): [number, number] {
  const u = projectToAxis(x, y, 'col', angleDeg);
  const v = projectToAxis(x, y, 'row', angleDeg);
  const col = Math.max(0, Math.min(colCount - 1, v_lines.filter(t => t < u).length));
  const row = Math.max(0, Math.min(rowCount - 1, h_lines.filter(t => t < v).length));
  return [col, row];
}

// ── Component ─────────────────────────────────────────────────────────────────

type LineMode = 'col' | 'row' | null;

interface EditingCell { col: number; row: number; left: number; top: number; }

interface Props {
  imageUrl: string;
  grid: GridData;
  onChange: (grid: GridData) => void;
}

const sliderMarks: Record<number, string> = { '-90': '-90°', '-45': '-45°', 0: '0°', 45: '45°', 90: '90°' };

const GridEditor: React.FC<Props> = ({ imageUrl, grid, onChange }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<any>(null);

  const [lineMode, setLineMode] = useState<LineMode>(null);
  const [hoverT, setHoverT] = useState<number | null>(null);
  const [hoverCell, setHoverCell] = useState<[number, number] | null>(null);
  const [editingCell, setEditingCell] = useState<EditingCell | null>(null);

  const colCount = grid.v_lines.length + 1;
  const rowCount = grid.h_lines.length + 1;
  const hasGrid = grid.v_lines.length > 0 || grid.h_lines.length > 0;

  const bounds = getImageBounds(grid.angle);
  const colBounds = [bounds.colMin, ...grid.v_lines, bounds.colMax];
  const rowBounds = [bounds.rowMin, ...grid.h_lines, bounds.rowMax];

  const getRelPos = useCallback((e: React.MouseEvent): [number, number] => {
    const rect = containerRef.current!.getBoundingClientRect();
    return [
      Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width)),
      Math.max(0, Math.min(1, (e.clientY - rect.top) / rect.height)),
    ];
  }, []);

  // ── handlers ────────────────────────────────────────────────────────────────

  const handleClick = (e: React.MouseEvent) => {
    const [x, y] = getRelPos(e);

    if (lineMode) {
      const t = projectToAxis(x, y, lineMode, grid.angle);
      if (lineMode === 'col') {
        onChange({ ...grid, v_lines: [...grid.v_lines, t].sort((a, b) => a - b) });
      } else {
        onChange({ ...grid, h_lines: [...grid.h_lines, t].sort((a, b) => a - b) });
      }
      return;
    }

    if (hasGrid) {
      const [col, row] = getCellAt(x, y, grid.angle, grid.v_lines, grid.h_lines, colCount, rowCount);
      const [cx, cy] = cellCenterInImage(col, row, grid.angle, colBounds, rowBounds);
      // Clamp popup anchor so it stays inside the image
      setEditingCell({
        col, row,
        left: Math.max(8, Math.min(92, cx * 100)),
        top: Math.max(8, Math.min(92, cy * 100)),
      });
      // focus input on next tick
      setTimeout(() => inputRef.current?.focus(), 30);
    }
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    const [x, y] = getRelPos(e);
    if (lineMode) {
      setHoverT(projectToAxis(x, y, lineMode, grid.angle));
      setHoverCell(null);
    } else if (hasGrid) {
      setHoverCell(getCellAt(x, y, grid.angle, grid.v_lines, grid.h_lines, colCount, rowCount));
      setHoverT(null);
    }
  };

  const handleMouseLeave = () => { setHoverT(null); setHoverCell(null); };

  const removeLine = (type: 'col' | 'row', idx: number) => {
    const newLabels: Record<string, string> = {};
    if (type === 'col') {
      const newV = grid.v_lines.filter((_, i) => i !== idx);
      for (let c = 0; c < newV.length + 1; c++) {
        const oldC = c <= idx ? c : c + 1;
        for (let r = 0; r < rowCount; r++) {
          const v = grid.labels[`${oldC}_${r}`]; if (v) newLabels[`${c}_${r}`] = v;
        }
      }
      onChange({ ...grid, v_lines: newV, labels: newLabels });
    } else {
      const newH = grid.h_lines.filter((_, i) => i !== idx);
      for (let c = 0; c < colCount; c++) {
        for (let r = 0; r < newH.length + 1; r++) {
          const oldR = r <= idx ? r : r + 1;
          const v = grid.labels[`${c}_${oldR}`]; if (v) newLabels[`${c}_${r}`] = v;
        }
      }
      onChange({ ...grid, h_lines: newH, labels: newLabels });
    }
  };

  const setLabel = (col: number, row: number, value: string) => {
    const newLabels = { ...grid.labels };
    if (value.trim() === '') delete newLabels[`${col}_${row}`];
    else newLabels[`${col}_${row}`] = value;
    onChange({ ...grid, labels: newLabels });
  };

  const commitEdit = () => setEditingCell(null);

  const switchLineMode = (m: LineMode) => {
    setLineMode(prev => prev === m ? null : m);
    setEditingCell(null);
    setHoverT(null);
    setHoverCell(null);
  };

  // ── SVG helpers ──────────────────────────────────────────────────────────────

  const ptsStr = (corners: [number, number][]) =>
    corners.map(([x, y]) => `${x * VW},${y * VH}`).join(' ');

  const visColPct = (t: number) =>
    `${((t - bounds.colMin) / (bounds.colMax - bounds.colMin) * 100).toFixed(0)}%`;
  const visRowPct = (t: number) =>
    `${((t - bounds.rowMin) / (bounds.rowMax - bounds.rowMin) * 100).toFixed(0)}%`;

  const hint = lineMode === 'col' ? 'Click to place a column line'
    : lineMode === 'row' ? 'Click to place a row line'
    : hasGrid ? 'Click a cell to label it'
    : 'Add column and row lines first, then click cells to label them';

  const cursor = lineMode ? 'crosshair' : hasGrid ? 'pointer' : 'default';

  // ── render ───────────────────────────────────────────────────────────────────

  return (
    <div>
      {/* Rotation */}
      <div style={{ marginBottom: 14, padding: '12px 16px', background: '#f6f8fa', borderRadius: 8, border: '1px solid #e8e8e8' }}>
        <Row gutter={12} align='middle'>
          <Col flex='none'>
            <Text strong style={{ fontSize: 12, whiteSpace: 'nowrap' }}>Grid Rotation</Text>
          </Col>
          <Col flex='1'>
            <Slider
              min={-90} max={90} step={0.5}
              value={grid.angle}
              onChange={(v) => onChange({ ...grid, angle: v })}
              marks={sliderMarks}
              tooltip={{ formatter: (v) => `${v}°` }}
            />
          </Col>
          <Col flex='none'>
            <InputNumber
              min={-90} max={90} step={0.5}
              value={grid.angle}
              onChange={(v) => onChange({ ...grid, angle: v ?? 0 })}
              style={{ width: 85 }} addonAfter='°' size='small'
            />
          </Col>
        </Row>
        <Text type='secondary' style={{ fontSize: 11 }}>
          Column and row lines are always perpendicular — rotate to align with the part
        </Text>
      </div>

      {/* Line mode toolbar */}
      <Space wrap style={{ marginBottom: 10 }}>
        <Button
          size='small'
          type={lineMode === 'col' ? 'primary' : 'default'}
          icon={<PlusOutlined />}
          onClick={() => switchLineMode('col')}
        >
          Column Line
        </Button>
        <Button
          size='small'
          type={lineMode === 'row' ? 'primary' : 'default'}
          icon={<PlusOutlined />}
          danger={lineMode === 'row'}
          onClick={() => switchLineMode('row')}
        >
          Row Line
        </Button>
        {lineMode && (
          <Button size='small' onClick={() => switchLineMode(null)}>Done</Button>
        )}
        <Text type='secondary' style={{ fontSize: 12 }}>{hint}</Text>
      </Space>

      {/* Image canvas */}
      <div style={{ position: 'relative' }}>
        {/* overflow:hidden container for the image + SVG */}
        <div
          ref={containerRef}
          style={{
            position: 'relative', userSelect: 'none', lineHeight: 0,
            cursor, borderRadius: 8, overflow: 'hidden', border: '1px solid #d9d9d9',
          }}
          onClick={handleClick}
          onMouseMove={handleMouseMove}
          onMouseLeave={handleMouseLeave}
        >
          <img src={imageUrl} alt='camera view' style={{ width: '100%', display: 'block' }} draggable={false} />

          <svg
            style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', pointerEvents: 'none' }}
            viewBox={`0 0 ${VW} ${VH}`}
            preserveAspectRatio='none'
          >
            {/* Hover cell highlight */}
            {!lineMode && hoverCell && !editingCell && (() => {
              const [c, r] = hoverCell;
              const corners = getCellCorners(c, r, grid.angle, colBounds, rowBounds);
              return (
                <polygon
                  points={ptsStr(corners)}
                  fill='rgba(255,255,255,0.18)'
                  stroke='rgba(255,255,255,0.7)'
                  strokeWidth={2}
                  strokeDasharray='6 3'
                />
              );
            })()}

            {/* Active (editing) cell highlight */}
            {editingCell && (() => {
              const corners = getCellCorners(editingCell.col, editingCell.row, grid.angle, colBounds, rowBounds);
              return (
                <polygon
                  points={ptsStr(corners)}
                  fill='rgba(24,144,255,0.2)'
                  stroke='#1890ff'
                  strokeWidth={2.5}
                />
              );
            })()}

            {/* Column lines */}
            {grid.v_lines.map((t, i) => {
              const pts = getLineEndpoints(t, 'col', grid.angle);
              if (!pts) return null;
              const [[x1, y1], [x2, y2]] = pts;
              const mx = (x1 + x2) / 2, my = (y1 + y2) / 2;
              return (
                <g key={`col_${i}`}>
                  <line x1={x1*VW} y1={y1*VH} x2={x2*VW} y2={y2*VH} stroke='rgba(0,0,0,0.35)' strokeWidth={4} />
                  <line x1={x1*VW} y1={y1*VH} x2={x2*VW} y2={y2*VH} stroke='#40a9ff' strokeWidth={2.5} strokeDasharray='14 6' />
                  <rect x={mx*VW-14} y={my*VH-12} width={28} height={24} rx={4} fill='#1890ff' />
                  <text x={mx*VW} y={my*VH+7} textAnchor='middle' fill='white' fontSize={14} fontWeight='bold'>{i + 1}</text>
                </g>
              );
            })}

            {/* Row lines */}
            {grid.h_lines.map((t, i) => {
              const pts = getLineEndpoints(t, 'row', grid.angle);
              if (!pts) return null;
              const [[x1, y1], [x2, y2]] = pts;
              const mx = (x1 + x2) / 2, my = (y1 + y2) / 2;
              return (
                <g key={`row_${i}`}>
                  <line x1={x1*VW} y1={y1*VH} x2={x2*VW} y2={y2*VH} stroke='rgba(0,0,0,0.35)' strokeWidth={4} />
                  <line x1={x1*VW} y1={y1*VH} x2={x2*VW} y2={y2*VH} stroke='#ff7875' strokeWidth={2.5} strokeDasharray='14 6' />
                  <rect x={mx*VW-14} y={my*VH-12} width={28} height={24} rx={4} fill='#f5222d' />
                  <text x={mx*VW} y={my*VH+7} textAnchor='middle' fill='white' fontSize={14} fontWeight='bold'>{String.fromCharCode(65 + i)}</text>
                </g>
              );
            })}

            {/* Hover preview line */}
            {lineMode && hoverT !== null && (() => {
              const pts = getLineEndpoints(hoverT, lineMode, grid.angle);
              if (!pts) return null;
              const [[x1, y1], [x2, y2]] = pts;
              return (
                <line x1={x1*VW} y1={y1*VH} x2={x2*VW} y2={y2*VH}
                  stroke={lineMode === 'col' ? '#1890ff' : '#f5222d'}
                  strokeWidth={2} strokeDasharray='8 5' opacity={0.45}
                />
              );
            })()}

            {/* Cell labels */}
            {hasGrid && Array.from({ length: colCount }, (_, c) =>
              Array.from({ length: rowCount }, (_, r) => {
                const label = grid.labels[`${c}_${r}`];
                if (!label) return null;
                const [cxRaw, cyRaw] = cellCenterInImage(c, r, grid.angle, colBounds, rowBounds);
                // Drop cells whose center is very far outside the image
                if (cxRaw < -0.25 || cxRaw > 1.25 || cyRaw < -0.25 || cyRaw > 1.25) return null;
                const lw = Math.max(label.length * 14 + 20, 44);
                // Clamp so the badge stays fully within the viewBox
                const xMargin = (lw / 2 + 4) / VW;
                const yMargin = 20 / VH;
                const cx = Math.max(xMargin, Math.min(1 - xMargin, cxRaw));
                const cy = Math.max(yMargin, Math.min(1 - yMargin, cyRaw));
                return (
                  <g key={`lbl_${c}_${r}`}>
                    <rect x={cx*VW - lw/2} y={cy*VH - 17} width={lw} height={34} rx={6} fill='rgba(0,0,0,0.55)' />
                    <text x={cx*VW} y={cy*VH + 8} textAnchor='middle' fill='white' fontSize={24} fontWeight='bold' fontFamily='monospace'>
                      {label}
                    </text>
                  </g>
                );
              })
            )}
          </svg>
        </div>

        {/* Floating cell label input — positioned over the image, outside overflow:hidden */}
        {editingCell && (
          <div
            style={{
              position: 'absolute',
              left: `${editingCell.left}%`,
              top: `${editingCell.top}%`,
              transform: 'translate(-50%, -50%)',
              zIndex: 20,
              background: 'white',
              borderRadius: 8,
              padding: '8px 10px',
              boxShadow: '0 4px 20px rgba(0,0,0,0.35)',
              border: '2px solid #1890ff',
              minWidth: 110,
              pointerEvents: 'all',
            }}
            // Prevent clicks inside the popup from propagating to the canvas
            onClick={(e) => e.stopPropagation()}
          >
            <Text style={{ fontSize: 11, display: 'block', marginBottom: 4, color: '#888', textAlign: 'center' }}>
              Col {editingCell.col + 1} · Row {String.fromCharCode(65 + editingCell.row)}
            </Text>
            <Input
              ref={inputRef}
              size='small'
              value={grid.labels[`${editingCell.col}_${editingCell.row}`] ?? ''}
              placeholder={`${String.fromCharCode(65 + editingCell.row)}${editingCell.col + 1}`}
              onChange={(e) => setLabel(editingCell.col, editingCell.row, e.target.value)}
              onBlur={commitEdit}
              onKeyDown={(e) => {
                if (e.key === 'Enter') commitEdit();
                if (e.key === 'Escape') commitEdit();
                if (e.key === 'Delete' || e.key === 'Backspace') {
                  // allow normal backspace, but if field is already empty, clear and close
                  if ((grid.labels[`${editingCell.col}_${editingCell.row}`] ?? '') === '') commitEdit();
                }
              }}
              style={{ textAlign: 'center', fontWeight: 700, fontSize: 16, letterSpacing: 1 }}
              allowClear
            />
            <Text type='secondary' style={{ fontSize: 10, display: 'block', textAlign: 'center', marginTop: 4 }}>
              Enter to confirm · Esc to cancel
            </Text>
          </div>
        )}
      </div>

      {/* Line management lists */}
      <Row gutter={16} style={{ marginTop: 14 }}>
        <Col span={12}>
          <Text strong style={{ fontSize: 12, display: 'block', marginBottom: 6, color: '#1890ff' }}>
            Column Lines ({grid.v_lines.length})
          </Text>
          {grid.v_lines.length === 0
            ? <Text type='secondary' style={{ fontSize: 11 }}>None placed</Text>
            : (
              <Space direction='vertical' size={4} style={{ width: '100%' }}>
                {grid.v_lines.map((t, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <div style={{ width: 18, height: 18, background: '#1890ff', borderRadius: 3, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <span style={{ color: 'white', fontSize: 10, fontWeight: 700 }}>{i + 1}</span>
                    </div>
                    <Text style={{ fontSize: 11, flex: 1 }}>at {visColPct(t)}</Text>
                    <Button size='small' type='text' danger icon={<DeleteOutlined />} onClick={() => removeLine('col', i)} />
                  </div>
                ))}
              </Space>
            )}
        </Col>
        <Col span={12}>
          <Text strong style={{ fontSize: 12, display: 'block', marginBottom: 6, color: '#f5222d' }}>
            Row Lines ({grid.h_lines.length})
          </Text>
          {grid.h_lines.length === 0
            ? <Text type='secondary' style={{ fontSize: 11 }}>None placed</Text>
            : (
              <Space direction='vertical' size={4} style={{ width: '100%' }}>
                {grid.h_lines.map((t, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <div style={{ width: 18, height: 18, background: '#f5222d', borderRadius: 3, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <span style={{ color: 'white', fontSize: 10, fontWeight: 700 }}>{String.fromCharCode(65 + i)}</span>
                    </div>
                    <Text style={{ fontSize: 11, flex: 1 }}>at {visRowPct(t)}</Text>
                    <Button size='small' type='text' danger icon={<DeleteOutlined />} onClick={() => removeLine('row', i)} />
                  </div>
                ))}
              </Space>
            )}
        </Col>
      </Row>

      {!hasGrid && (
        <Text type='secondary' style={{ display: 'block', marginTop: 12, fontSize: 12, textAlign: 'center' }}>
          Adjust rotation to match the part, then add Column and Row lines to build the coordinate grid
        </Text>
      )}
    </div>
  );
};

export default GridEditor;
